/**
 * I18nProvider — mounted once at the app root (see main.tsx).
 *
 * State lives in the zustand locale store, so this provider deliberately holds
 * no context value; its job is to guarantee the document-level side effects
 * (`<html lang>` + `<html dir>`) stay in sync with the active locale across the
 * React lifecycle — including the first render after hydration and any change
 * driven from anywhere in the tree.
 *
 * It does NOT re-key/remount its subtree: components that read copy via `useT()`
 * subscribe to the locale store and re-render on their own, so a language switch
 * updates the UI without tearing down page state (open menus, form input, etc.).
 */

import { useEffect, type ReactNode } from 'react';

import { applyLocaleToDocument, useLocaleStore } from './store';

export function I18nProvider({ children }: { children: ReactNode }) {
  const locale = useLocaleStore((s) => s.locale);

  useEffect(() => {
    applyLocaleToDocument(locale);
  }, [locale]);

  return <>{children}</>;
}
